import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Eye, EyeOff, Briefcase, CheckCircle2, ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRedirectIfAuthed } from "@/hooks/useRedirectIfAuthed";

const STEPS = ["Account", "Payout Info"] as const;

const SalesRegister = () => {
  useRedirectIfAuthed();
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    payoutMethod: "",
    payoutHandle: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateAccount = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.firstName.trim()) errs.firstName = "First name required";
    if (!formData.lastName.trim()) errs.lastName = "Last name required";
    if (!formData.email.trim()) errs.email = "Email required";
    if (!formData.password || formData.password.length < 6) errs.password = "Password must be at least 6 characters";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAccountStep = async () => {
    if (!validateAccount()) return;
    setIsLoading(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: formData.email,
      password: formData.password,
      options: { emailRedirectTo: `${window.location.origin}/sales` },
    });

    if (signUpError) {
      toast({ title: "Registration failed", description: signUpError.message, variant: "destructive" });
      setIsLoading(false);
      return;
    }

    const newUserId = signUpData.user?.id;
    if (!newUserId) {
      toast({ title: "Error", description: "Could not create your account.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    setUserId(newUserId);

    // Update profile name if session exists (auto-confirmed)
    if (signUpData.session) {
      await supabase.from("profiles").update({
        first_name: formData.firstName,
        last_name: formData.lastName,
      }).eq("user_id", newUserId);
    }

    setIsLoading(false);
    setCurrentStep(1);
  };

  const handlePayoutStep = async () => {
    if (!userId) return;
    setIsLoading(true);

    // Insert role request via backend function (works for unconfirmed users)
    const { data: roleReqData, error: roleReqError } = await supabase.functions.invoke("create-role-request", {
      body: {
        user_id: userId,
        requested_role: "salesperson",
        metadata: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          payout_method: formData.payoutMethod,
          payout_handle: formData.payoutHandle,
        },
      },
    });
    const requestId = roleReqData?.id || crypto.randomUUID();

    // Notify admins via email
    const { data: adminEmails } = await supabase.rpc("get_user_emails");
    const { data: adminRoles } = await supabase.from("user_roles").select("user_id").eq("role", "admin");
    const adminUserIds = new Set((adminRoles || []).map((r) => r.user_id));
    const admins = (adminEmails || []).filter((u) => adminUserIds.has(u.user_id));

    for (const admin of admins) {
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "new-role-request",
          recipientEmail: admin.email,
          idempotencyKey: `role-request-${requestId}-${admin.user_id}`,
          templateData: {
            applicantName: `${formData.firstName} ${formData.lastName}`,
            applicantEmail: formData.email,
            requestedRole: "salesperson",
          },
        },
      });
    }

    setIsLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-border/50 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="text-primary" size={32} />
            </div>
            <h2 className="font-display text-2xl text-foreground">Application Submitted!</h2>
            <p className="text-muted-foreground">
              Your salesperson account request has been submitted. An admin will review and approve your account shortly.
              You'll receive an email once approved.
            </p>
            <Link to="/">
              <Button variant="outline" className="mt-4">Return to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Briefcase size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl text-foreground">Sales Partner Registration</h1>
            <p className="text-muted-foreground text-sm">Join our referral team</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEPS.map((step, i) => (
              <span key={step} className={`text-xs font-medium ${i <= currentStep ? "text-primary" : "text-muted-foreground"}`}>
                {step}
              </span>
            ))}
          </div>
          <Progress value={((currentStep + 1) / STEPS.length) * 100} className="h-2" />
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardContent className="pt-6 space-y-4">
            {currentStep === 0 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input id="firstName" value={formData.firstName} onChange={(e) => updateField("firstName", e.target.value)} />
                    {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input id="lastName" value={formData.lastName} onChange={(e) => updateField("lastName", e.target.value)} />
                    {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => updateField("password", e.target.value)} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                </div>
                <Button onClick={handleAccountStep} disabled={isLoading} className="w-full gap-2">
                  {isLoading ? "Creating Account…" : <>Continue <ArrowRight size={16} /></>}
                </Button>
              </>
            )}

            {currentStep === 1 && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="payoutMethod">Preferred Payout Method</Label>
                  <Input id="payoutMethod" placeholder="e.g. PayPal, Venmo, Bank Transfer" value={formData.payoutMethod} onChange={(e) => updateField("payoutMethod", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="payoutHandle">Payout Handle / Details</Label>
                  <Input id="payoutHandle" placeholder="e.g. your@email.com or account details" value={formData.payoutHandle} onChange={(e) => updateField("payoutHandle", e.target.value)} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Payout information is optional now — you can update it later from your dashboard settings.
                </p>
                <Button onClick={handlePayoutStep} disabled={isLoading} className="w-full gap-2">
                  {isLoading ? "Submitting…" : <>Submit Application <CheckCircle2 size={16} /></>}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default SalesRegister;
